/**
 * Chutt... - ChartExplainer
 * Wraps any chart and adds an info icon with a rich tooltip popover.
 */
import React, { useState, useRef } from 'react';
import { Box, Typography, Popper, Fade, IconButton, Chip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { colors } from '../../styles/colors';
import { chartExplainerContent } from '../../data/chartExplainerContent';

function ChartExplainer({ chartId, position = 'top-right', children }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const content = chartExplainerContent[chartId];
  if (!content) {
    // No explainer content for this chart — just render children
    return children;
  }

  const positionStyles = {
    'top-right': { top: 4, right: 4 },
    'top-left': { top: 4, left: 4 },
    'bottom-right': { bottom: 4, right: 4 },
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {children}
      <IconButton
        ref={anchorRef}
        size="small"
        aria-label="Aide"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          position: 'absolute',
          ...positionStyles[position] || positionStyles['top-right'],
          zIndex: 10,
          p: 0.5,
          opacity: 0.6,
          transition: 'opacity 0.2s',
          '&:hover': { opacity: 1, bgcolor: 'transparent' },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 18, color: colors.gray400 }} />
      </IconButton>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
        sx={{ zIndex: 1300 }}
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Box
              onMouseEnter={() => setOpen(true)}
              onMouseLeave={() => setOpen(false)}
              sx={{
                maxWidth: 340,
                p: '16px 20px',
                bgcolor: colors.charcoal,
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              }}
            >
              {/* Title */}
              <Typography
                sx={{
                  fontWeight: 700,
                  color: colors.white,
                  fontSize: '0.85rem',
                  mb: 1,
                }}
              >
                {content.title}
              </Typography>

              {/* Description */}
              <Typography
                sx={{
                  color: colors.gray300,
                  fontSize: '0.78rem',
                  lineHeight: 1.5,
                  mb: content.mission ? 1 : 1.5,
                }}
              >
                {content.description}
              </Typography>

              {/* Mission badge */}
              {content.mission && (
                <Chip
                  label={`Mission : ${content.mission}`}
                  size="small"
                  sx={{
                    mb: 1.5,
                    height: 22,
                    borderRadius: '9999px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    bgcolor: 'rgba(46,158,107,0.15)',
                    color: '#4DB87F',
                    border: '1px solid rgba(46,158,107,0.25)',
                  }}
                />
              )}

              {/* How to read */}
              {content.howToRead && (
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <MenuBookOutlinedIcon sx={{ fontSize: 14, color: colors.primaryLight }} />
                    <Typography sx={{ fontWeight: 600, color: colors.primaryLight, fontSize: '0.75rem' }}>
                      Comment lire
                    </Typography>
                  </Box>
                  <Typography sx={{ color: colors.gray300, fontSize: '0.75rem', lineHeight: 1.5 }}>
                    {content.howToRead}
                  </Typography>
                </Box>
              )}

              {/* Actions */}
              {content.actions && content.actions.length > 0 && (
                <Box>
                  <Typography sx={{ fontWeight: 600, color: colors.warning, fontSize: '0.75rem', mb: 0.5 }}>
                    Actions recommandees
                  </Typography>
                  {content.actions.map((action, idx) => (
                    <Box key={idx} sx={{ display: 'flex', gap: 0.5, mb: 0.3 }}>
                      <Typography sx={{ color: colors.warning, fontSize: '0.7rem', lineHeight: 1.5 }}>
                        •
                      </Typography>
                      <Typography sx={{ color: colors.gray300, fontSize: '0.72rem', lineHeight: 1.5 }}>
                        {action}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Fade>
        )}
      </Popper>
    </Box>
  );
}

export default ChartExplainer;
